import type {
  AcceptInvitationResponse,
  CreateInvitationRequest,
  CreateOrgRequest,
  Invitation,
  InvitationPreview,
  OrgRole,
  Organisation,
  OrganisationMember,
  UpdateOrgRequest,
} from "@models/organisation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invitationsApi, orgsApi } from "../services/api";

/** Query keys centralised so invalidation is consistent across hooks. */
export const orgKeys = {
  all: ["orgs"] as const,
  lists: () => [...orgKeys.all, "list"] as const,
  detail: (id: string) => [...orgKeys.all, "detail", id] as const,
  members: (id: string) => [...orgKeys.all, "members", id] as const,
  invitations: (id: string) => [...orgKeys.all, "invitations", id] as const,
  preview: (token: string) => [...orgKeys.all, "preview", token] as const,
};

/** List of orgs the current user is a member of. */
export function useOrgs() {
  return useQuery<Organisation[]>({
    queryKey: orgKeys.lists(),
    queryFn: orgsApi.list,
    // Slightly longer stale time than projects — org membership rarely
    // changes mid-session. Still subject to invalidation on mutations.
    staleTime: 60_000,
  });
}

/** Single org detail. */
export function useOrg(orgId: string | null | undefined) {
  return useQuery<Organisation>({
    queryKey: orgId ? orgKeys.detail(orgId) : ["orgs", "detail", "_none"],
    // biome-ignore lint/style/noNonNullAssertion: enabled gate above ensures orgId is set when queryFn runs
    queryFn: () => orgsApi.get(orgId!),
    enabled: Boolean(orgId),
  });
}

export function useOrgMembers(orgId: string | null | undefined) {
  return useQuery<OrganisationMember[]>({
    queryKey: orgId ? orgKeys.members(orgId) : ["orgs", "members", "_none"],
    // biome-ignore lint/style/noNonNullAssertion: enabled gate above ensures orgId is set when queryFn runs
    queryFn: () => orgsApi.listMembers(orgId!),
    enabled: Boolean(orgId),
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOrgRequest) => orgsApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.lists() });
    },
  });
}

export function useUpdateOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOrgRequest) => orgsApi.update(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.lists() });
      void qc.invalidateQueries({ queryKey: orgKeys.detail(orgId) });
    },
  });
}

export function useDeleteOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => orgsApi.delete(orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.lists() });
    },
  });
}

// ── Member management (Phase 1b) ────────────────────────────────────────────

export function useUpdateMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { targetUserId: string; role: OrgRole }) =>
      orgsApi.updateMemberRole(orgId, vars.targetUserId, vars.role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
      void qc.invalidateQueries({ queryKey: orgKeys.detail(orgId) });
    },
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (targetUserId: string) =>
      orgsApi.removeMember(orgId, targetUserId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.members(orgId) });
      void qc.invalidateQueries({ queryKey: orgKeys.detail(orgId) });
      void qc.invalidateQueries({ queryKey: orgKeys.lists() });
    },
  });
}

// ── Invitations (Phase 1b) ──────────────────────────────────────────────────

export function useInvitations(orgId: string | null | undefined) {
  return useQuery<Invitation[]>({
    queryKey: orgId
      ? orgKeys.invitations(orgId)
      : ["orgs", "invitations", "_none"],
    // biome-ignore lint/style/noNonNullAssertion: enabled gate above ensures orgId is set when queryFn runs
    queryFn: () => orgsApi.listInvitations(orgId!),
    enabled: Boolean(orgId),
  });
}

export function useCreateInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInvitationRequest) =>
      orgsApi.createInvitation(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.invitations(orgId) });
    },
  });
}

export function useRevokeInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      orgsApi.revokeInvitation(orgId, invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.invitations(orgId) });
    },
  });
}

export function useInvitationPreview(token: string | null | undefined) {
  return useQuery<InvitationPreview>({
    queryKey: token ? orgKeys.preview(token) : ["orgs", "preview", "_none"],
    // biome-ignore lint/style/noNonNullAssertion: enabled gate above ensures token is set when queryFn runs
    queryFn: () => invitationsApi.preview(token!),
    enabled: Boolean(token),
    // Invitation state mutates when accepted — refetch on mount so a
    // stale "already_member=false" doesn't mislead a returning user.
    staleTime: 0,
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation<AcceptInvitationResponse, unknown, string>({
    mutationFn: (token: string) => invitationsApi.accept(token),
    onSuccess: () => {
      // New membership landed — refresh the org list so the sidebar
      // switcher picks it up immediately.
      void qc.invalidateQueries({ queryKey: orgKeys.lists() });
    },
  });
}
